import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import { isPosModuleEnabled } from '../config/featureFlags';
import '../App.css';
import './PosTicketsAdmin.css';

type PosSite = { siteId: string; label: string };

type TicketRow = {
  id: number;
  clientUuid: string;
  status: string;
  tabName: string | null;
  tableName?: string | null;
  areaName?: string | null;
  guestCount: number;
  serverDisplayName?: string | null;
  subtotal: number;
  taxAmount: number;
  tipAmount: number;
  total: number;
  taxRatePercent: number | null;
  openedAt: string | null;
  closedAt: string | null;
};

type TicketDetail = TicketRow & {
  lines: {
    id: number;
    name: string;
    unitPrice: number;
    quantity: number;
    seat: number | null;
    voided: boolean;
    comped: boolean;
    taxable: boolean;
    kitchenStatus: string;
    modifiers: { name: string; price: number }[];
  }[];
  discounts: { name: string; amount: number; percent: number | null }[];
  payments: { method: string; amount: number; tipAmount: number; status: string }[];
};

function money(n: number) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function ticketLabel(t: TicketRow) {
  if (t.tabName) return t.tabName;
  if (t.tableName) return t.tableName;
  return `Ticket #${t.id}`;
}

function statusTone(status: string) {
  if (status === 'open') return 'open';
  if (status === 'sent') return 'sent';
  if (status === 'voided') return 'voided';
  return 'closed';
}

const PosTicketsAdmin: React.FC = () => {
  const [sites, setSites] = useState<PosSite[]>([]);
  const [siteId, setSiteId] = useState('TAPROOM-MADISON');
  const [statusFilter, setStatusFilter] = useState('');
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    setError(null);
    try {
      const qs = new URLSearchParams({ siteId, day: today });
      if (statusFilter) qs.set('status', statusFilter);
      const [statusRes, ticketsRes] = await Promise.all([
        apiFetch('/api/pos/status'),
        apiFetch(`/api/pos/tickets?${qs.toString()}`),
      ]);
      const status = await statusRes.json();
      const rows = await ticketsRes.json();
      setSites(Array.isArray(status.sites) ? status.sites : []);
      setTickets(Array.isArray(rows) ? rows : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    }
  }, [siteId, statusFilter, today]);

  useEffect(() => {
    if (isPosModuleEnabled()) load();
  }, [load]);

  const openDetail = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/pos/tickets/${id}`);
      setDetail(await res.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setBusy(false);
    }
  };

  if (!isPosModuleEnabled()) {
    return (
      <div className="page-container container">
        <h2>POS Tickets</h2>
        <p>The POS module is turned off.</p>
      </div>
    );
  }

  const siteOptions =
    sites.length > 0
      ? sites
      : [
          { siteId: 'TAPROOM-MADISON', label: 'Madison' },
          { siteId: 'TAPROOM-ATHENS', label: 'Athens' },
        ];

  const openCount = tickets.filter((t) => t.status === 'open' || t.status === 'sent').length;
  const closedCount = tickets.filter((t) => t.status === 'closed' || t.status === 'voided').length;

  return (
    <div className="page-container container pos-tickets-admin">
      <h2>POS Tickets</h2>
      <p className="text-muted">
        Read-only view of today&apos;s open and closed tickets. Checkout and inventory deduction
        come in a later PR — this does not touch sales orders or distributor flow.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-2 align-items-end" style={{ marginBottom: 16 }}>
        <div className="col-md-3">
          <label className="form-label">Site</label>
          <select
            className="form-select"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
          >
            {siteOptions.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All today</option>
            <option value="open">Open</option>
            <option value="sent">Sent</option>
            <option value="closed">Closed</option>
            <option value="voided">Voided</option>
          </select>
        </div>
        <div className="col-md-6 text-muted" style={{ paddingBottom: 6 }}>
          {today} · {openCount} open/sent · {closedCount} closed/voided
        </div>
      </div>

      <div className="pos-tickets-mobile">
        <div className="pos-ticket-list">
          {tickets.map((t) => (
            <button
              type="button"
              key={t.id}
              className="pos-ticket-row"
              onClick={() => openDetail(t.id)}
              disabled={busy}
            >
              <span className="pos-ticket-row__main">
                <span className="pos-ticket-row__title">{ticketLabel(t)}</span>
                <span className="pos-ticket-row__meta">
                  {t.areaName || '—'} · {t.guestCount} guests
                  {t.serverDisplayName ? ` · ${t.serverDisplayName}` : ''}
                </span>
              </span>
              <span className={`pos-ticket-chip pos-ticket-chip--${statusTone(t.status)}`}>
                {t.status}
              </span>
              <span className="pos-ticket-row__total">{money(t.total)}</span>
              <span aria-hidden>›</span>
            </button>
          ))}
          {!tickets.length && (
            <div className="p-3 text-muted">No tickets for today.</div>
          )}
        </div>
      </div>

      <div className="pos-tickets-desktop table-responsive">
        <table className="table table-sm table-striped">
          <thead>
            <tr>
              <th>Table / tab</th>
              <th>Area</th>
              <th>Status</th>
              <th>Guests</th>
              <th>Server</th>
              <th>Subtotal</th>
              <th>Tax</th>
              <th>Total</th>
              <th>Opened</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id}>
                <td>{ticketLabel(t)}</td>
                <td>{t.areaName || '—'}</td>
                <td>{t.status}</td>
                <td>{t.guestCount}</td>
                <td>{t.serverDisplayName || '—'}</td>
                <td>{money(t.subtotal)}</td>
                <td>{money(t.taxAmount)}</td>
                <td>{money(t.total)}</td>
                <td style={{ fontSize: 12 }}>{t.openedAt || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => openDetail(t.id)}
                    disabled={busy}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {!tickets.length && (
              <tr>
                <td colSpan={10} className="text-muted">
                  No tickets for today.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div
          className="pos-ticket-detail-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDetail(null);
          }}
        >
          <div className="pos-ticket-detail-sheet">
            <h3>{ticketLabel(detail)}</h3>
            <p className="pos-ticket-detail-sheet__sub">
              {detail.status} · {detail.areaName || 'No area'} · tax{' '}
              {detail.taxRatePercent == null ? '—' : `${detail.taxRatePercent}%`}
            </p>

            <div className="pos-ticket-detail-field">
              <span className="pos-ticket-detail-field__label">Guests</span>
              {detail.guestCount}
            </div>
            <div className="pos-ticket-detail-field">
              <span className="pos-ticket-detail-field__label">Server</span>
              {detail.serverDisplayName || '—'}
            </div>
            <div className="pos-ticket-detail-field">
              <span className="pos-ticket-detail-field__label">Opened</span>
              {detail.openedAt || '—'}
            </div>
            {detail.closedAt && (
              <div className="pos-ticket-detail-field">
                <span className="pos-ticket-detail-field__label">Closed</span>
                {detail.closedAt}
              </div>
            )}

            <h4 style={{ fontSize: '1rem', marginTop: 16 }}>Lines</h4>
            {(detail.lines || []).length === 0 && (
              <p className="text-muted">No lines.</p>
            )}
            <ul className="list-unstyled">
              {(detail.lines || []).map((line) => (
                <li key={line.id} style={{ marginBottom: 8, fontSize: 14 }}>
                  <strong>
                    {line.quantity}× {line.name}
                  </strong>{' '}
                  {money(line.unitPrice)}
                  {line.seat != null ? ` · seat ${line.seat}` : ''}
                  {line.voided ? ' · voided' : ''}
                  {line.comped ? ' · comped' : ''}
                  {!line.taxable ? ' · non-taxable' : ''}
                  {(line.modifiers || []).length > 0 && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {line.modifiers.map((m) => `${m.name} (${money(m.price)})`).join(', ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="pos-ticket-detail-field">
              <span className="pos-ticket-detail-field__label">Subtotal</span>
              {money(detail.subtotal)}
            </div>
            <div className="pos-ticket-detail-field">
              <span className="pos-ticket-detail-field__label">Tax</span>
              {money(detail.taxAmount)}
            </div>
            <div className="pos-ticket-detail-field">
              <span className="pos-ticket-detail-field__label">Tip</span>
              {money(detail.tipAmount)}
            </div>
            <div className="pos-ticket-detail-field">
              <span className="pos-ticket-detail-field__label">Total</span>
              <strong>{money(detail.total)}</strong>
            </div>

            {(detail.payments || []).length > 0 && (
              <>
                <h4 style={{ fontSize: '1rem', marginTop: 12 }}>Payments</h4>
                <ul>
                  {detail.payments.map((p, idx) => (
                    <li key={idx}>
                      {p.method}: {money(p.amount)} tip {money(p.tipAmount)} ({p.status})
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="pos-ticket-detail-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setDetail(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosTicketsAdmin;
